import { ChangeEvent, FC, useMemo, useState } from 'react';
import { noOp } from '../../lib/utils';
import { ValidatedStringMessage } from '../ValidatedStringMessage/ValidatedStringMessage';

export type TextareaProps = {
  /**
   * Placeholder of the input
   */
  placeholder?: string;
  /**
   * Flag to disable input
   */
  disabled?: boolean;
  /**
   * Flag for error
   */
  error?: boolean;
  /**
   * Rows of text area
   */
  rows?: number;
  /**
   * Maximum length for input
   */
  maxLength?: number;
  /**
   * Change action of the input
   */
  onChange?(event: ChangeEvent<HTMLTextAreaElement>): void;
  /**
   * Focus action of the input
   */
  onFocus?(): void;
  /**
   * Blur action of the input
   */
  onBlur?(): void;
  /**
   * On key down action of the input
   */
  onKeyDown?(): void;
  /**
   * Get validation result for the Input
   */
  onValidate?(isValid: boolean): void;
  initialValue?: string;
};

const BASE_TEXTAREA_CLASSES =
  'font-regular rounded p-2.5 border bg-white outline-none resize-y w-full h-full';
const DEFAULT_TEXTAREA_CLASSES = `${BASE_TEXTAREA_CLASSES} border-gray-500 placeholder-gray-600 text-gray-700 hover:border-blue-100 focus:border-blue-100`;
const DISABLED_TEXTAREA_CLASSES = `${BASE_TEXTAREA_CLASSES} border-gray-200 placeholder-gray-400 text-gray-400`;
const ERROR_TEXTAREA_CLASSES = `${BASE_TEXTAREA_CLASSES} border-red-200 placeholder-gray-600 text-gray-700 hover:border-blue-100 focus:border-blue-100`;

export const Textarea: FC<TextareaProps> = ({
  placeholder,
  disabled,
  error,
  rows = 1,
  maxLength = Infinity,
  initialValue = '',
  onChange = noOp,
  onFocus,
  onBlur,
  onKeyDown,
  onValidate = noOp,
}: TextareaProps) => {
  const [value, setValue] = useState<string>(initialValue);
  const [isValid, setIsValid] = useState(true);

  const className = useMemo(() => {
    switch (true) {
      case disabled:
        return DISABLED_TEXTAREA_CLASSES;
      case error:
      case !isValid:
        return ERROR_TEXTAREA_CLASSES;
      default:
        return DEFAULT_TEXTAREA_CLASSES;
    }
  }, [disabled, error, isValid]);

  return (
    <>
      <textarea
        className={className}
        value={value}
        placeholder={placeholder}
        disabled={disabled}
        onKeyDown={onKeyDown}
        onChange={(event: ChangeEvent<HTMLTextAreaElement>) => {
          setValue(event?.target?.value || '');
          onChange(event);
        }}
        onFocus={onFocus}
        onBlur={onBlur}
        rows={rows}
      />
      <ValidatedStringMessage
        maxLength={maxLength}
        valueLength={value.length}
        onValidate={(valid) => {
          setIsValid(valid);
          onValidate(valid);
        }}
      />
    </>
  );
};
